import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { X, CheckCircle2, HardDrive, Loader2 } from 'lucide-react';
import { projectsService } from '../../services/projects';
import { FileDropzone } from '../common/FileDropzone';
import { GoogleSheetsInput } from '../common/GoogleSheetsInput';

interface DataModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  projectId: string;
  onUploadSuccess: (taskId?: string) => void;
}

const DataModal: React.FC<DataModalProps> = ({ isOpen, onClose, isDark, projectId, onUploadSuccess }) => {
  const { language } = useLanguage();
  const isEs = (language as string) === 'es';

  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadTaskId, setUploadTaskId] = useState<string | undefined>();
  // Are the datasheet's times UTC or already airport-local? It cannot be inferred
  // from the values, and the *_UTC column naming is an assumption, not evidence.
  // Declared here, where the user is looking at their own file. Default UTC.
  const [timeBasis, setTimeBasis] = useState<'utc' | 'local'>('utc');
  const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white';
  const textMain = 'text-[var(--text-primary)]';

  if (!isOpen) return null;

  // 1. Local file upload (.xlsx / .csv)
  const handleLocalUpload = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const file = files[0];
      const res = await projectsService.uploadFile(projectId, file, timeBasis);
      setUploadTaskId(res.task_id);
      setUploadSuccess(true);
      // Immediately notify parent so progress tracking starts
      if (res.task_id) {
        onUploadSuccess(res.task_id);
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      const isTimeout = error?.code === 'ECONNABORTED' || detail.toLowerCase().includes('timeout');
      const msg = isTimeout
        ? (isEs
            ? "Tiempo de espera agotado. Verifica que el servidor (Docker) esté activo e inténtalo de nuevo."
            : "Request timed out. Make sure the server (Docker) is running and try again.")
        : (isEs
            ? `Error al subir el archivo:\n${detail}`
            : `Error uploading file:\n${detail}`);
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  // 2. Google Sheets (URL)
  const handleSheetConnect = async (url: string) => {
    setUploading(true);
    try {
      const res = await projectsService.connectSheet(projectId, url, timeBasis);
      if (res.task_id) {
        onUploadSuccess(res.task_id);
      } else {
        setUploadSuccess(true);
        onUploadSuccess();
      }
    } catch (error: any) {
      console.error("Error connecting Google Sheet:", error);
      const errorMsg = error?.response?.data?.detail || error?.message || "Unknown error";
      alert(`Could not connect to Google Sheet:\n${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-32 bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className={`w-full max-w-xl rounded-xl border shadow-2xl overflow-hidden flex flex-col ${cardBg}`}>

        {/* HEADER */}
        <div className={`px-6 py-4 border-b flex justify-between items-center ${'border-[var(--border)]'}`}>
          <h3 className={`text-lg font-bold ${textMain}`}>{isEs ? 'Ingesta de Datos' : 'Data Ingestion'}</h3>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-red-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* CONTENT */}
        <div className="p-6">
          {uploadSuccess ? (
            <div className="py-8 flex flex-col items-center justify-center text-center space-y-4 animate-fade-in">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle2 size={32} />
              </div>
              <div>
                <h4 className={`text-xl font-bold ${textMain}`}>{isEs ? 'Carga Completada' : 'Upload Complete'}</h4>
                <p className="text-[var(--text-tertiary)] text-sm mt-1">
                  {isEs ? 'Datos recibidos correctamente.' : 'Data received successfully.'}<br />
                  {isEs ? 'El análisis ha comenzado en segundo plano.' : 'Analysis has started in the background.'}
                </p>
              </div>
              <button
                onClick={() => onClose()}
                className="mt-6 w-full py-3 bg-gradient-to-r from-[#F26430] to-[#E85D04] text-white rounded-lg font-bold hover:opacity-90 transition-all shadow-lg active:scale-[0.98]"
              >
                OK
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* TIME BASIS — declared before the file is supplied, because it
                  cannot be inferred from the data and getting it wrong
                  double-shifts every time in the analysis. */}
              <div className="rounded-lg border p-3"
                   style={{ background: 'var(--surface-raised)', borderColor: 'var(--border)' }}>
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-1"
                   style={{ color: 'var(--text-tertiary)' }}>
                  {isEs ? 'Base horaria del datasheet' : 'Datasheet time basis'}
                </p>
                <p className="text-[11px] mb-2.5" style={{ color: 'var(--text-secondary)' }}>
                  {isEs
                    ? 'Las horas de tu fichero, ¿están en UTC o ya en hora local del aeropuerto? No se puede deducir del dato.'
                    : "Are your file's times in UTC or already in the airport's local time? It cannot be inferred from the data."}
                </p>
                <div className="inline-flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                  {(['utc', 'local'] as const).map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setTimeBasis(b)}
                      disabled={uploading}
                      className="px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
                      style={{
                        background: timeBasis === b ? 'var(--accent-soft)' : 'transparent',
                        color: timeBasis === b ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                      }}
                    >
                      {b === 'utc' ? 'UTC' : (isEs ? 'Hora local' : 'Local time')}
                    </button>
                  ))}
                </div>
              </div>

              {/* LOCAL FILE */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-blue-100 rounded-md text-blue-600">
                    <HardDrive size={16} />
                  </div>
                  <span className={`text-sm font-semibold ${textMain}`}>{isEs ? 'Subir archivo histórico' : 'Upload historical file'}</span>
                </div>
                <FileDropzone
                  onDrop={handleLocalUpload}
                  isLoading={uploading}
                />
              </div>

              {/* SEPARATOR */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className={`w-full border-t ${'border-[var(--border)]'}`}></div>
                </div>
                <div className="relative flex justify-center">
                  <span className={`px-2 text-sm text-[var(--text-tertiary)] ${isDark ? 'bg-white' : 'bg-white'}`}>
                    {isEs ? 'O conecta una fuente cloud' : 'Or connect a cloud source'}
                  </span>
                </div>
              </div>

              {/* GOOGLE SHEETS */}
              <div>
                <GoogleSheetsInput
                  onUrlSubmit={handleSheetConnect}
                  isLoading={uploading}
                />
              </div>
            </div>
          )}
        </div>

        {/* FOOTER LOADING */}
        {uploading && !uploadSuccess && (
          <div className="px-6 py-3 bg-blue-50 border-t border-blue-100 flex items-center justify-center gap-3">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-sm text-blue-700 font-medium">{isEs ? 'Procesando datos... por favor espere.' : 'Processing data... please wait.'}</span>
          </div>
        )}

      </div>
    </div>
  );
};

export default DataModal;
